/**
 * Mident Smile Analyzer v1
 * Client-side smile analysis widget for conversion optimization
 * NO medical claims, NO backend, NO data persistence
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION CONSTANTS
  // ============================================================================
  
  const CONFIG = {
    // Analysis thresholds (0-100 scale)
    THRESHOLDS: {
      BRIGHTNESS_LOW: 45,      // Below this = darker teeth
      BRIGHTNESS_MEDIUM: 65,   // Medium range
      YELLOW_RATIO_HIGH: 1.15, // R/B ratio indicating yellow tint
      YELLOW_RATIO_MEDIUM: 1.08,
      CONTRAST_LOW: 25,        // Low contrast = matte surface
      CONTRAST_MEDIUM: 40,
      VARIANCE_HIGH: 800,      // High variance = uneven coloring
      VARIANCE_MEDIUM: 400
    },
    
    // Animation settings
    ANIMATION: {
      COUNTDOWN_DURATION: 3000, // 3 seconds
      ANALYSIS_PHASES: [
        'Detecting smile...',
        'Analyzing enamel color...',
        'Evaluating tooth surface...',
        'Finalizing results...'
      ]
    },
    
    // Product recommendations
    PRODUCTS: {
      MAIN: {
        name: 'Mident Watersonic',
        benefits: [
          'Removes up to 10× more stains than manual brushing',
          'Professional results in 14 days',
          'Gentle on sensitive teeth'
        ],
        cta: 'Improve Your Smile',
        url: 'https://midentcare.com/products/watersonic-by-mident'
      },
      UPSELLS: [
        {
          name: 'Replacement Steel Heads',
          url: '/products/replacement-steel-heads-for-the-watersonic'
        }
      ]
    }
  };

  // ============================================================================
  // ENGLISH COPY
  // ============================================================================
  
  const COPY = {
    HERO: {
      title: 'Discover Your Smile\'s Condition in 10 Seconds',
      subtitle: 'Upload a photo of your smile and get an instant analysis + personalized recommendations',
      cta: 'Try it Free'
    },
    UPLOAD: {
      dragText: 'Drag photo here',
      orText: 'or',
      buttonText: 'Start Smile Analysis',
      helperText: 'Take a photo or choose from your gallery'
    },
    ANALYSIS: {
      title: 'Analyzing your smile...',
      subtitle: 'Just a moment, evaluating visual appearance'
    },
    RESULTS: {
      title: 'Your Results',
      subtitle: 'Visual Analysis Overview',
      disclaimer: 'This is not a medical diagnosis, only a cosmetic analysis of visual appearance.'
    },
    INSIGHTS: {
      brightness_low: {
        label: 'Moderate Discoloration',
        description: 'Visually darker enamel shade',
        score: 45
      },
      brightness_medium: {
        label: 'Medium Brightness',
        description: 'Moderately light tone',
        score: 65
      },
      brightness_high: {
        label: 'Bright Enamel',
        description: 'Visually lighter shade',
        score: 85
      },
      yellow_high: {
        label: 'Yellow Pigmentation',
        description: 'Possible effects from coffee, tea, or smoking',
        score: 50
      },
      yellow_medium: {
        label: 'Light Pigmentation',
        description: 'Minor staining from food or beverages',
        score: 70
      },
      yellow_low: {
        label: 'Minimal Pigmentation',
        description: 'Natural white shade',
        score: 90
      },
      contrast_low: {
        label: 'Matte Surface',
        description: 'Enamel appears less glossy',
        score: 55
      },
      contrast_medium: {
        label: 'Medium Gloss',
        description: 'Natural enamel shine',
        score: 75
      },
      contrast_high: {
        label: 'Glossy Enamel',
        description: 'Visually smooth and shiny surface',
        score: 90
      },
      variance_high: {
        label: 'Uneven Shade',
        description: 'Visible color differences between teeth',
        score: 50
      },
      variance_medium: {
        label: 'Slightly Uneven Shade',
        description: 'Minor shade variations',
        score: 70
      },
      variance_low: {
        label: 'Even Shade',
        description: 'Consistent enamel color',
        score: 90
      }
    },
    RECOMMENDATION: {
      title: 'Recommendations for Your Smile',
      subtitle: 'Based on visual analysis',
      mainProduct: 'Main Recommendation',
      alsoConsider: 'Complementary Product'
    },
    RETRY: {
      button: 'Try Another Photo',
      newAnalysis: 'New Analysis'
    }
  };

  // ============================================================================
  // IMAGE ANALYSIS ENGINE
  // ============================================================================
  
  class SmileAnalyzer {
    constructor(imageElement) {
      this.image = imageElement;
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d');
    }

    /**
     * Analyze the uploaded smile photo using Canvas API
     * Returns object with brightness, yellowRatio, contrast, variance
     */
    analyze() {
      // Draw image to canvas
      this.canvas.width = this.image.width;
      this.canvas.height = this.image.height;
      this.ctx.drawImage(this.image, 0, 0);

      // Get pixel data from center region (where teeth likely are)
      const centerX = Math.floor(this.canvas.width * 0.25);
      const centerY = Math.floor(this.canvas.height * 0.4);
      const width = Math.floor(this.canvas.width * 0.5);
      const height = Math.floor(this.canvas.height * 0.3);

      const imageData = this.ctx.getImageData(centerX, centerY, width, height);
      const pixels = imageData.data;

      return {
        brightness: this._calculateBrightness(pixels),
        yellowRatio: this._calculateYellowRatio(pixels),
        contrast: this._calculateContrast(pixels),
        variance: this._calculateVariance(pixels)
      };
    }

    /**
     * Calculate average brightness (0-100)
     */
    _calculateBrightness(pixels) {
      let totalBrightness = 0;
      let count = 0;

      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        
        // Convert to perceived brightness
        const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
        totalBrightness += brightness;
        count++;
      }

      return (totalBrightness / count) / 255 * 100;
    }

    /**
     * Calculate yellow tint ratio (red/blue ratio)
     */
    _calculateYellowRatio(pixels) {
      let totalRed = 0;
      let totalBlue = 0;
      let count = 0;

      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const b = pixels[i + 2];
        
        totalRed += r;
        totalBlue += b;
        count++;
      }

      const avgRed = totalRed / count;
      const avgBlue = totalBlue / count;

      // Avoid division by zero
      return avgBlue > 0 ? avgRed / avgBlue : 1.0;
    }

    /**
     * Calculate contrast (standard deviation of brightness)
     */
    _calculateContrast(pixels) {
      const brightnesses = [];
      
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
        brightnesses.push(brightness);
      }

      const mean = brightnesses.reduce((a, b) => a + b, 0) / brightnesses.length;
      const variance = brightnesses.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / brightnesses.length;
      
      return Math.sqrt(variance) / 255 * 100;
    }

    /**
     * Calculate color variance (how uneven the coloring is)
     */
    _calculateVariance(pixels) {
      const colors = [];
      
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        colors.push({ r, g, b });
      }

      // Sample every 10th pixel for performance
      const samples = colors.filter((_, idx) => idx % 10 === 0);
      
      const avgR = samples.reduce((sum, c) => sum + c.r, 0) / samples.length;
      const avgG = samples.reduce((sum, c) => sum + c.g, 0) / samples.length;
      const avgB = samples.reduce((sum, c) => sum + c.b, 0) / samples.length;

      const variance = samples.reduce((sum, c) => {
        return sum + Math.pow(c.r - avgR, 2) + Math.pow(c.g - avgG, 2) + Math.pow(c.b - avgB, 2);
      }, 0) / samples.length;

      return variance;
    }
  }

  // ============================================================================
  // RULE-BASED INSIGHTS GENERATOR
  // ============================================================================
  
  class InsightsGenerator {
    constructor(analysisResults) {
      this.results = analysisResults;
      this.thresholds = CONFIG.THRESHOLDS;
    }

    /**
     * Generate 2-4 personalized insights based on analysis
     */
    generate() {
      const insights = [];

      // Brightness insight
      insights.push(this._getBrightnessInsight());

      // Yellow tint insight
      insights.push(this._getYellowInsight());

      // Contrast insight
      insights.push(this._getContrastInsight());

      // Variance insight (only if significant)
      if (this.results.variance > this.thresholds.VARIANCE_MEDIUM) {
        insights.push(this._getVarianceInsight());
      }

      return insights;
    }

    _getBrightnessInsight() {
      const brightness = this.results.brightness;
      
      if (brightness < this.thresholds.BRIGHTNESS_LOW) {
        return COPY.INSIGHTS.brightness_low;
      } else if (brightness < this.thresholds.BRIGHTNESS_MEDIUM) {
        return COPY.INSIGHTS.brightness_medium;
      } else {
        return COPY.INSIGHTS.brightness_high;
      }
    }

    _getYellowInsight() {
      const yellowRatio = this.results.yellowRatio;
      
      if (yellowRatio > this.thresholds.YELLOW_RATIO_HIGH) {
        return COPY.INSIGHTS.yellow_high;
      } else if (yellowRatio > this.thresholds.YELLOW_RATIO_MEDIUM) {
        return COPY.INSIGHTS.yellow_medium;
      } else {
        return COPY.INSIGHTS.yellow_low;
      }
    }

    _getContrastInsight() {
      const contrast = this.results.contrast;
      
      if (contrast < this.thresholds.CONTRAST_LOW) {
        return COPY.INSIGHTS.contrast_low;
      } else if (contrast < this.thresholds.CONTRAST_MEDIUM) {
        return COPY.INSIGHTS.contrast_medium;
      } else {
        return COPY.INSIGHTS.contrast_high;
      }
    }

    _getVarianceInsight() {
      const variance = this.results.variance;
      
      if (variance > this.thresholds.VARIANCE_HIGH) {
        return COPY.INSIGHTS.variance_high;
      } else if (variance > this.thresholds.VARIANCE_MEDIUM) {
        return COPY.INSIGHTS.variance_medium;
      } else {
        return COPY.INSIGHTS.variance_low;
      }
    }
  }

  // ============================================================================
  // UI CONTROLLER
  // ============================================================================
  
  class SmileAnalyzerUI {
    constructor(containerSelector) {
      this.container = document.querySelector(containerSelector);
      if (!this.container) {
        console.error('Smile Analyzer container not found:', containerSelector);
        return;
      }
      
      this.uploadedImage = null;
      this.init();
    }

    init() {
      this.showUploadScreen();
      this.attachEventListeners();
    }

    /**
     * Dispatch custom analytics events
     */
    dispatchEvent(eventName, detail = {}) {
      const event = new CustomEvent(`smileAnalyzer:${eventName}`, {
        detail: detail,
        bubbles: true
      });
      document.dispatchEvent(event);
    }

    attachEventListeners() {
      // File input change
      const fileInput = this.container.querySelector('.sa-file-input');
      if (fileInput) {
        fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
      }
    }


    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    }

    processFile(file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        alert('Please upload an image (JPG or PNG)');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('File is too large. Maximum size is 10 MB.');
        return;
      }

      // Read file
      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploadedImage = new Image();
        this.uploadedImage.onload = () => {
          // Dispatch analytics event
          this.dispatchEvent('photoUploaded', { fileSize: file.size, fileType: file.type });
          this.startAnalysis();
        };
        this.uploadedImage.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }

    startAnalysis() {
      this.showAnalyzingScreen();
      
      // Simulate AI processing with countdown
      let countdown = 3;
      let phaseIndex = 0;
      
      const countdownEl = this.container.querySelector('.sa-countdown');
      const phaseEl = this.container.querySelector('.sa-phase-text');
      const progressBar = this.container.querySelector('.sa-progress-bar');
      
      const interval = setInterval(() => {
        countdown--;
        
        if (countdownEl) countdownEl.textContent = countdown;
        
        // Update phase text
        if (phaseEl && CONFIG.ANIMATION.ANALYSIS_PHASES[phaseIndex]) {
          phaseEl.textContent = CONFIG.ANIMATION.ANALYSIS_PHASES[phaseIndex];
          phaseIndex++;
        }
        
        // Update progress bar
        if (progressBar) {
          const progress = ((3 - countdown) / 3) * 100;
          progressBar.style.width = progress + '%';
        }
        
        if (countdown === 0) {
          clearInterval(interval);
          
          // Perform actual analysis
          setTimeout(() => {
            this.performAnalysis();
          }, 500);
        }
      }, 1000);
    }

    performAnalysis() {
      // Run analysis
      const analyzer = new SmileAnalyzer(this.uploadedImage);
      const results = analyzer.analyze();
      
      // Generate insights
      const insightsGenerator = new InsightsGenerator(results);
      const insights = insightsGenerator.generate();
      
      // Dispatch analytics event
      this.dispatchEvent('analysisComplete', {
        insightsCount: insights.length,
        brightness: Math.round(results.brightness),
        yellowRatio: results.yellowRatio.toFixed(2),
        contrast: Math.round(results.contrast),
        variance: Math.round(results.variance)
      });
      
      // Show results
      this.showResultsScreen(insights, results);
    }

    showUploadScreen() {
      this.container.innerHTML = `
        <div class="sa-screen sa-upload-screen">
          <div class="sa-card">
            <div class="sa-upload-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                <circle cx="12" cy="13" r="4"></circle>
              </svg>
            </div>
            
            <h1 class="sa-title">${COPY.HERO.title}</h1>
            <p class="sa-subtitle">${COPY.HERO.subtitle}</p>
            
            <div class="sa-features">
              <div class="sa-feature">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>Takes 60 seconds</span>
              </div>
              <div class="sa-feature">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <span>Personalized insights</span>
              </div>
              <div class="sa-feature">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
                <span>Expert recommendations</span>
              </div>
            </div>
            
            <div class="sa-upload-actions">
              <label class="sa-upload-button sa-primary-upload">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                  <circle cx="12" cy="13" r="4"></circle>
                </svg>
                ${COPY.UPLOAD.buttonText}
                <input type="file" class="sa-file-input" accept="image/*" capture="environment">
              </label>
              
              <p class="sa-helper-text">${COPY.UPLOAD.helperText}</p>
            </div>
          </div>
        </div>
      `;
      
      this.attachEventListeners();
    }

    showAnalyzingScreen() {
      this.container.innerHTML = `
        <div class="sa-screen sa-analyzing-screen">
          <div class="sa-card">
            <div class="sa-analyzing-content">
              <div class="sa-spinner">
                <div class="sa-spinner-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"></path>
                  </svg>
                </div>
              </div>
              
              <h2 class="sa-analyzing-title">${COPY.ANALYSIS.title}</h2>
              <p class="sa-analyzing-subtitle">${COPY.ANALYSIS.subtitle}</p>
              
              <div class="sa-countdown-wrapper">
                <span class="sa-countdown">3</span>
              </div>
              
              <p class="sa-phase-text">${CONFIG.ANIMATION.ANALYSIS_PHASES[0]}</p>
              
              <div class="sa-progress-wrapper">
                <div class="sa-progress-bar"></div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    showResultsScreen(insights, rawResults) {
      const insightsHTML = insights.map(insight => `
        <div class="sa-insight-card">
          <div class="sa-insight-header">
            <h4 class="sa-insight-label">${insight.label}</h4>
            <span class="sa-insight-score">${insight.score}%</span>
          </div>
          <p class="sa-insight-description">${insight.description}</p>
          <div class="sa-insight-bar">
            <div class="sa-insight-bar-fill" style="width: ${insight.score}%"></div>
          </div>
        </div>
      `).join('');

      const upsellsHTML = CONFIG.PRODUCTS.UPSELLS.map((product, index) => `
        <a href="${product.url}" class="sa-upsell-item" data-product="upsell-${index}">
          ${product.name}
        </a>
      `).join('');

      this.container.innerHTML = `
        <div class="sa-screen sa-results-screen">
          <div class="sa-card">
            <!-- Results Section -->
            <div class="sa-results-section">
              <h2 class="sa-section-title">${COPY.RESULTS.title}</h2>
              <p class="sa-section-subtitle">${COPY.RESULTS.subtitle}</p>
              
              <div class="sa-insights-grid">
                ${insightsHTML}
              </div>
              
              <p class="sa-disclaimer">${COPY.RESULTS.disclaimer}</p>
            </div>

            <!-- Recommendation Section -->
            <div class="sa-recommendation-section">
              <h3 class="sa-section-title">${COPY.RECOMMENDATION.title}</h3>
              <p class="sa-section-subtitle">${COPY.RECOMMENDATION.subtitle}</p>
              
              <!-- Main Product Card -->
              <div class="sa-product-card">
                <div class="sa-product-badge">${COPY.RECOMMENDATION.mainProduct}</div>
                <h4 class="sa-product-name">${CONFIG.PRODUCTS.MAIN.name}</h4>
                
                <ul class="sa-product-benefits">
                  ${CONFIG.PRODUCTS.MAIN.benefits.map(benefit => `
                    <li class="sa-benefit-item">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>${benefit}</span>
                    </li>
                  `).join('')}
                </ul>
                
                <a href="${CONFIG.PRODUCTS.MAIN.url}" class="sa-cta-button" data-product="main">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                  </svg>
                  ${CONFIG.PRODUCTS.MAIN.cta}
                </a>
              </div>

              <!-- Upsells -->
              <div class="sa-upsells">
                <p class="sa-upsells-title">${COPY.RECOMMENDATION.alsoConsider}</p>
                <div class="sa-upsells-grid">
                  ${upsellsHTML}
                </div>
              </div>
            </div>

            <!-- Retry Button -->
            <button class="sa-retry-button" onclick="window.smileAnalyzerInstance.init()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M1 4v6h6M23 20v-6h-6"></path>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
              </svg>
              ${COPY.RETRY.button}
            </button>
          </div>
        </div>
      `;
      
      // Attach analytics event listeners
      this.attachResultsEventListeners();
    }

    attachResultsEventListeners() {
      // Track CTA clicks
      const ctaButton = this.container.querySelector('.sa-cta-button');
      if (ctaButton) {
        ctaButton.addEventListener('click', (e) => {
          const product = e.currentTarget.getAttribute('data-product');
          this.dispatchEvent('ctaClicked', { product });
        });
      }

      // Track upsell clicks
      const upsellItems = this.container.querySelectorAll('.sa-upsell-item');
      upsellItems.forEach(item => {
        item.addEventListener('click', (e) => {
          const product = e.currentTarget.getAttribute('data-product');
          this.dispatchEvent('ctaClicked', { product });
        });
      });

      // Track retry clicks
      const retryButton = this.container.querySelector('.sa-retry-button');
      if (retryButton) {
        retryButton.addEventListener('click', () => {
          this.dispatchEvent('retryClicked');
        });
      }
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  function initSmileAnalyzer() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        window.smileAnalyzerInstance = new SmileAnalyzerUI('#smile-analyzer-widget');
      });
    } else {
      window.smileAnalyzerInstance = new SmileAnalyzerUI('#smile-analyzer-widget');
    }
  }

  // Auto-initialize
  initSmileAnalyzer();

  // Export for manual initialization if needed
  window.SmileAnalyzer = {
    init: initSmileAnalyzer,
    UI: SmileAnalyzerUI,
    Engine: SmileAnalyzer,
    InsightsGenerator: InsightsGenerator,
    CONFIG: CONFIG,
    COPY: COPY
  };

})();

